/**
 * Flatten a hast root into a per-block array suitable for windowed rendering
 * by `@tanstack/react-virtual`. Each entry holds:
 *   - the React element for one top-level block (heading / paragraph / list /
 *     fenced code / table / blockquote / embed / math-display)
 *   - the source line stamped by `rehypeSourceLines` (for editor↔preview
 *     scroll-sync)
 *   - the ids of any headings inside the block (for `#anchor` navigation
 *     when the target is offscreen and not yet mounted)
 *   - a stable React key (source offset if present; otherwise index)
 *
 * Why "via the React tree" rather than calling `processor.stringify` per
 * child? Doing one big stringify call keeps unified's allocation overhead
 * low (one pipeline run, not N). `rehype-react` returns a Fragment whose
 * children are the per-block React elements in the same order as
 * `hast.children`; we just zip the two.
 */
import { Children, type ReactElement, type ReactNode } from 'react';
import type { Root as HastRoot, RootContent, Element as HastElement } from 'hast';

export interface PreviewBlock {
  /** Position in the flat block array. */
  index: number;
  /** Stable React key — source-offset based when available. */
  key: string;
  /** 1-based source line of this block, or null if the block has none. */
  line: number | null;
  /** Heading/element ids inside this block (for anchor lookup). */
  ids: string[];
  /** The React element to render in the virtualized slot. */
  element: ReactNode;
}

export interface BlockIndexMaps {
  /** Heading slug → block index. Built from rehype-slug ids. */
  bySlug: Map<string, number>;
  /** 1-based source line → block index. Built from rehypeSourceLines stamps. */
  byLine: Map<number, number>;
}

export interface FlattenedPreview {
  blocks: PreviewBlock[];
  maps: BlockIndexMaps;
}

/**
 * Pull `data-line` from a hast element's properties. `rehypeSourceLines`
 * stamps it as a string on the hast properties object (camel-cased by
 * unified's property name policy → `dataLine`).
 */
function readLine(node: RootContent): number | null {
  if (node.type !== 'element') return null;
  const props = (node as HastElement).properties;
  if (!props) return null;
  const raw = props.dataLine ?? props['data-line' as keyof typeof props];
  if (typeof raw === 'string') {
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  return null;
}

/** Walk a hast subtree and collect every element `id` (rehype-slug etc.). */
function collectIds(node: RootContent, out: string[]): void {
  if (node.type !== 'element') return;
  const el = node as HastElement;
  const id = el.properties?.id;
  if (typeof id === 'string' && id) out.push(id);
  for (const child of el.children) collectIds(child, out);
}

/**
 * Best-effort stable key. `position.start.offset` (set by remark-parse) is
 * unique per top-level block; fall back to index when missing (e.g. nodes
 * synthesized by raw-HTML re-parse). Including the index suffix keeps the
 * key unique even when offsets collide.
 */
function makeKey(node: RootContent, index: number): string {
  const start = node.position?.start;
  if (start && typeof start.offset === 'number') return `${start.offset}|${index}`;
  return `idx:${index}`;
}

/**
 * Zip the hast root's top-level children with the per-block React elements
 * produced by `rehype-react`. The full element is the Fragment returned by
 * `processor.stringify(hastRoot)` — its React children array is parallel to
 * `hastRoot.children`.
 */
export function flattenBlocks(hast: HastRoot, fullElement: ReactElement): FlattenedPreview {
  const reactChildren = Children.toArray(
    (fullElement.props as { children?: ReactNode }).children,
  );
  const blocks: PreviewBlock[] = [];
  const bySlug = new Map<string, number>();
  const byLine = new Map<number, number>();

  for (let i = 0; i < hast.children.length; i++) {
    const node = hast.children[i];
    const line = readLine(node);
    const ids: string[] = [];
    collectIds(node, ids);

    blocks.push({
      index: i,
      key: makeKey(node, i),
      line,
      ids,
      element: reactChildren[i] ?? null,
    });

    if (line !== null && !byLine.has(line)) byLine.set(line, i);
    for (const id of ids) {
      if (!bySlug.has(id)) bySlug.set(id, i);
    }
  }

  return { blocks, maps: { bySlug, byLine } };
}
